import type { SvgIconComponent } from '@mui/icons-material'
import React from 'react'
import styled from 'styled-components'
import { Close } from 'sutra/components/Icons'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'

export type ChicletTone =
  | 'gray'
  | 'white'
  | 'blue'
  | 'emerald'
  | 'violet'
  | 'cerulean'
  | 'warning'
  | 'danger'
  | 'navy'
export type ChicletSize = 'sm' | 'md' | 'lg'

/**
 * Extends the span's own attributes so `data-*`, `aria-*`, `title`, `id`,
 * `onClick` and `ref` reach the DOM node (a Sutra `Tooltip` child needs them).
 * `color` is omitted on purpose: antd's `<Tag color>` has no equivalent here,
 * so a leftover `color="red"` is a type error instead of a stray DOM attribute
 * (translate it with `antdColorTone`).
 */
export interface ChicletProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'color'
> {
  $tone?: ChicletTone
  $size?: ChicletSize
  /** Same as `$tone`, and wins over it. Use this one on a `styled(Chiclet)`:
   *  styled-components drops every `$`-prefixed prop before it reaches a
   *  wrapped component, so a `$tone` set there never arrives. */
  tone?: ChicletTone
  /** Same as `$size`, and wins over it; use it on a `styled(Chiclet)` too. */
  size?: ChicletSize
  /** An MUI icon component (sized by the chiclet) or an already-built element,
   *  which is rendered as given inside a slot that sizes its `svg`. */
  icon?: SvgIconComponent | React.ReactElement | null
  /** When set, renders a trailing × button that calls this on click. Omitted
   *  (the default) keeps today's display-only chiclet unchanged. */
  onRemove?: () => void
  /** aria-label for the × button. Required in practice when onRemove is set;
   *  falls back to a generic 'Remove' if omitted. */
  removeLabel?: string
}

// Foreground/background/border per tone, verified against WCAG AA (≥4.5:1) via
// sutra/utils/contrast.ts's getContrastRatio. `warning` and `danger` reuse
// Callout's exact bg/border shades; `gray`/`violet`/`cerulean` (chiclet-only,
// no Callout equivalent) follow the same "border one step darker than bg"
// pattern. `warning` needs `goldenLight[800]` (not [700]) for text — that
// family's lightness ramp sits higher at every step than the others.
const toneColors: Record<
  ChicletTone,
  { fg: string; bg: string; border: string }
> = {
  gray: { fg: gray[700], bg: gray[100], border: gray[200] },
  white: { fg: gray[700], bg: brand.white, border: gray[300] },
  blue: {
    fg: accents.reflectionBlue[700],
    bg: accents.reflectionBlue[100],
    border: accents.reflectionBlue[200],
  },
  emerald: {
    fg: accents.emeraldAura[700],
    bg: accents.emeraldAura[100],
    border: accents.emeraldAura[200],
  },
  violet: {
    fg: accents.violetHorizon[700],
    bg: accents.violetHorizon[100],
    border: accents.violetHorizon[200],
  },
  cerulean: {
    fg: accents.ceruleanFlow[700],
    bg: accents.ceruleanFlow[100],
    border: accents.ceruleanFlow[200],
  },
  warning: {
    fg: accents.goldenLight[800],
    bg: accents.goldenLight[100],
    border: accents.goldenLight[400],
  },
  danger: {
    fg: accents.crimsonBloom[700],
    bg: accents.crimsonBloom[100],
    border: accents.crimsonBloom[400],
  },
  navy: {
    fg: brand.stillNight[800],
    bg: brand.stillNight[50],
    border: brand.stillNight[200],
  },
}

const sizeStyles: Record<
  ChicletSize,
  { fontSize: string; padding: string; iconSize: string }
> = {
  sm: { fontSize: '10px', padding: '1px 6px', iconSize: '11px' },
  md: { fontSize: '11px', padding: '2px 8px', iconSize: '12px' },
  lg: { fontSize: '14px', padding: '3px 10px', iconSize: '14px' },
}

const StyledChiclet = styled.span<{
  $tone: ChicletTone
  $size: ChicletSize
}>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  white-space: nowrap;
  border-radius: ${radii.xs}px;
  font-weight: 500;
  font-size: ${({ $size }) => sizeStyles[$size].fontSize};
  padding: ${({ $size }) => sizeStyles[$size].padding};
  color: ${({ $tone }) => toneColors[$tone].fg};
  background: ${({ $tone }) => toneColors[$tone].bg};
  border: 1px solid ${({ $tone }) => toneColors[$tone].border};
`

// Gives an element icon (antd's, or an MUI one with no `sx`) the same box a
// component icon gets from `sx`.
const IconSlot = styled.span<{ $size: ChicletSize }>`
  display: inline-flex;
  align-items: center;

  svg {
    width: ${({ $size }) => sizeStyles[$size].iconSize};
    height: ${({ $size }) => sizeStyles[$size].iconSize};
  }
`

// Nested inside the flex root above, so it centers vertically against the
// label with no extra layout work — no separate alignment rule needed here.
const RemoveButton = styled.button<{
  $tone: ChicletTone
  $size: ChicletSize
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ $tone }) => toneColors[$tone].fg};
  cursor: pointer;

  svg {
    width: ${({ $size }) => sizeStyles[$size].iconSize};
    height: ${({ $size }) => sizeStyles[$size].iconSize};
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 1px;
  }
`

// `isValidElement` is a type guard on `ReactElement`, not on the union, so the
// component branch is narrowed by hand. An MUI icon is a memo/forwardRef object,
// not a function, so `typeof === 'function'` would be wrong.
const renderIcon = (
  icon: ChicletProps['icon'],
  size: ChicletSize,
): React.ReactNode => {
  if (!icon) return null
  if (React.isValidElement(icon)) {
    return <IconSlot $size={size}>{icon}</IconSlot>
  }
  const Icon = icon as SvgIconComponent
  return <Icon sx={{ fontSize: sizeStyles[size].iconSize }} />
}

export const Chiclet = React.forwardRef<HTMLSpanElement, ChicletProps>(
  (
    {
      $tone = 'gray',
      $size = 'md',
      tone = $tone,
      size = $size,
      icon,
      onRemove,
      removeLabel = 'Remove',
      children,
      ...rest
    },
    ref,
  ) => (
    <StyledChiclet ref={ref} $tone={tone} $size={size} {...rest}>
      {renderIcon(icon, size)}
      {children}
      {onRemove && (
        <RemoveButton
          type="button"
          $tone={tone}
          $size={size}
          aria-label={removeLabel}
          onClick={(event) => {
            // The × sits inside a span that may carry its own onClick, so
            // removing must not also fire the body click.
            event.stopPropagation()
            onRemove()
          }}
        >
          <Close />
        </RemoveButton>
      )}
    </StyledChiclet>
  ),
)

Chiclet.displayName = 'Chiclet'
